"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MediasoupPlayer = void 0;
const log_node_1 = require("@video/log-node");
const mobx_1 = require("mobx");
const feature_1 = require("../../api/player/features/feature");
const errors_1 = require("../errors");
const errors_deprecated_1 = require("../errors-deprecated");
const mediasoup_source_1 = require("../mediasoup-source");
const peer_1 = require("../peer");
const browser_1 = require("../utils/browser-support/browser");
const core_1 = require("./core");
const helper_1 = require("./helper");
class MediasoupPlayer extends core_1.CorePlayer {
    get displayName() {
        return MediasoupPlayer.displayName;
    }
    static async isSupported(logger) {
        return (0, browser_1.supportsMediasoupWebrtc)("Mediasoup Player", logger);
    }
    async isSupported() {
        return MediasoupPlayer.isSupported();
    }
    static get format() {
        return "webrtc";
    }
    get format() {
        return MediasoupPlayer.format;
    }
    get implementedFeatures() {
        if (this.isManifestPlayer) {
            return [feature_1.Feature.CONSUMER, feature_1.Feature.BITRATE_SWITCHING, feature_1.Feature.MUTED_AUTOPLAY];
        }
        return [feature_1.Feature.CONSUMER, feature_1.Feature.BITRATE_SWITCHING];
    }
    constructor(ctx, provider, options) {
        super(ctx, provider, options);
        this.preferredSource = null;
        this.hasAudio = false;
        this.hasVideo = false;
        this.isManifestPlayer = false;
        (0, mobx_1.makeObservable)(this, {
            hasAudio: mobx_1.observable,
            hasVideo: mobx_1.observable,
            consumerAudioEnabled: mobx_1.computed,
            consumerVideoEnabled: mobx_1.computed,
            noConsumerAudioAndVideo: mobx_1.computed,
        });
        this.playerOptions = options;
        if (this.playerOptions.mutedAutoplayFallback) {
            this.isManifestPlayer = true;
        }
        this.addInnerDisposer((0, mobx_1.reaction)(() => this.availableQualities, (qty) => {
            this.emit("layers", (0, helper_1.getBitrateLayersFromQualities)(qty));
        }, {
            equals: mobx_1.comparer.structural,
        }));
        this.addInnerDisposer((0, mobx_1.reaction)(() => this.currentQuality, (qty) => {
            var _a;
            this.emit("activeLayer", (_a = qty === null || qty === void 0 ? void 0 : qty.layer) !== null && _a !== void 0 ? _a : null);
            if (this.provider instanceof mediasoup_source_1.MediasoupSource && this.provider.peer instanceof peer_1.Peer && (qty === null || qty === void 0 ? void 0 : qty.layer) != null) {
                try {
                    this.provider.peer.setPreferredEncoding(qty.layer);
                }
                catch (err) {
                    this.emitError((0, errors_1.createError)(errors_deprecated_1.ErrorCode.UnableSwitchQuality, "Failed to change quality for webrtc consumer after currentQuality is changed", {
                        player: this.displayName,
                        format: this.format,
                        preferredLevel: qty.level,
                    }));
                }
            }
        }, {
            equals: mobx_1.comparer.structural,
        }));
        if (this.provider instanceof mediasoup_source_1.MediasoupSource) {
            this.provider.on("availableQualities", this.handleAvailableQualities);
            this.provider.on("accessDenied", this.handleAccessDenied);
        }
        this.addInnerDisposer(() => {
            if (this.provider instanceof mediasoup_source_1.MediasoupSource) {
                this.provider.off("availableQualities", this.handleAvailableQualities);
                this.provider.off("accessDenied", this.handleAccessDenied);
            }
        });
        this.ctx.logger.trace("constructor()", { options });
    }
    async handleSource(stream) {
        if (this.suspended) {
            return;
        }
        this.ctx.logger.trace("handleSource()");
        this.source = stream;
        if (this.elementSupervisor != null) {
            if (this.autoPlay || !this.internalPaused) {
                await this.playingPromise;
                this.ctx.logger.trace("handleSource() -> await this.playingPromise");
                this.elementSupervisor.updateSrcObject(stream);
                await this.play();
                this.ctx.logger.trace("handleSource() -> await play()");
            }
            else {
                this.elementSupervisor.updateSrcObject(stream);
            }
            this.ctx.logger.debug("srcObject set");
        }
        if (this.provider instanceof mediasoup_source_1.MediasoupSource) {
            this.hasAudio = this.provider.hasAudio;
            this.hasVideo = this.provider.hasVideo;
            this.consumerAudioMuted = !this.hasAudio || this.provider.audioMuted;
            this.consumerVideoPaused = !this.hasVideo || this.provider.videoPaused;
            this.availableQualities = this.provider.availableQualities;
        }
    }
    handleAccessDenied(response) {
        var _a;
        if (this.provider instanceof mediasoup_source_1.MediasoupSource) {
            (_a = this.provider.peer.call) === null || _a === void 0 ? void 0 : _a.dispose(response.message);
        }
    }
    handleAvailableQualities(ev) {
        this.availableQualities = ev;
    }
    get consumerAudioEnabled() {
        const { consumerAudioMuted, hasAudio } = this;
        if (!(this.provider instanceof mediasoup_source_1.MediasoupSource)) {
            return false;
        }
        return hasAudio && consumerAudioMuted === false;
    }
    get consumerVideoEnabled() {
        const { consumerVideoPaused, hasVideo } = this;
        if (!(this.provider instanceof mediasoup_source_1.MediasoupSource)) {
            return false;
        }
        return hasVideo && consumerVideoPaused === false;
    }
    get noConsumerAudioAndVideo() {
        const { consumerAudioMuted, hasAudio, consumerVideoPaused, hasVideo } = this;
        return !(hasAudio && consumerAudioMuted === false) && !(hasVideo && consumerVideoPaused === false);
    }
    get streamName() {
        if (this.provider instanceof mediasoup_source_1.MediasoupSource) {
            return this.provider.streamName;
        }
        return "";
    }
    get peerId() {
        var _a, _b;
        if (this.provider instanceof mediasoup_source_1.MediasoupSource) {
            return (_b = (_a = this.provider.peer) === null || _a === void 0 ? void 0 : _a.peerId) !== null && _b !== void 0 ? _b : "";
        }
        return "";
    }
    get callId() {
        var _a, _b, _c;
        if (this.provider instanceof mediasoup_source_1.MediasoupSource) {
            return (_c = (_b = (_a = this.provider.peer) === null || _a === void 0 ? void 0 : _a.call) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : "";
        }
        return "";
    }
    get userId() {
        var _a, _b;
        if (this.provider instanceof mediasoup_source_1.MediasoupSource) {
            return (_b = (_a = this.provider.peer) === null || _a === void 0 ? void 0 : _a.userId) !== null && _b !== void 0 ? _b : "";
        }
        return "";
    }
    toJSON() {
        var _a;
        const data = super.toJSON();
        if (!(data != null && typeof data === "object" && !Array.isArray(data) && !(0, log_node_1.isSerializableObject)(data))) {
            return {};
        }
        const aggregates = (_a = data.aggregates) !== null && _a !== void 0 ? _a : {};
        if (!(aggregates != null &&
            typeof aggregates === "object" &&
            !Array.isArray(aggregates) &&
            !(0, log_node_1.isSerializableObject)(aggregates))) {
            return data;
        }
        delete data.aggregates;
        return {
            ...data,
            options: this.options,
            aggregates: {
                ...aggregates,
                ...(0, log_node_1.extractAggregates)(this.provider, "support"),
                support: this.ctx.support.hash,
                consumerAudioEnabled: this.consumerAudioEnabled,
                consumerVideoEnabled: this.consumerVideoEnabled,
                noConsumerAudioAndVideo: this.noConsumerAudioAndVideo,
                preferredSource: this.preferredSource,
                currentQuality: this.currentQuality,
                isManifestPlayer: this.isManifestPlayer,
                peerId: this.peerId,
                userId: this.userId,
            },
        };
    }
}
exports.MediasoupPlayer = MediasoupPlayer;
MediasoupPlayer.displayName = "MediasoupPlayer";
//# sourceMappingURL=mediasoup.js.map